import React, { useState } from 'react';
import axios from 'axios';
import { Plus, Loader2, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { API_BASE } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';

const API = `${API_BASE}/api`;

/**
 * AddToPathwayButton - Button to add content to user's Competency Learning Pathway
 * 
 * Usage:
 * <AddToPathwayButton
 *   itemType="course" // course, learning_lab, masterclass, campaign
 *   itemId="course_123"
 *   itemTitle="Course Title"
 *   isInPathway={true} // Optional: pre-populate if already in pathway
 * />
 */
export default function AddToPathwayButton({ 
  itemType, 
  itemId, 
  itemTitle,
  isInPathway = false,
  variant = "outline",
  size = "sm",
  className = ""
}) {
  const { token, isAuthenticated } = useAuth();
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState(isInPathway);
  
  const headers = { Authorization: `Bearer ${token}` };
  
  const handleAddToCompetencyPathway = async () => {
    if (!isAuthenticated) {
      toast.error('Please sign in to add items to your competency pathway');
      return;
    }
    
    setAdding(true);
    try {
      const response = await axios.post(`${API}/competencies/my-learning-pathway/add`, {
        item_type: itemType,
        item_id: itemId,
        item_title: itemTitle
      }, { headers });
      
      if (response.data.already_added) {
        toast.info('Already in your Competency Learning Pathway');
        setAdded(true);
      } else {
        toast.success('Added to Competency Learning Pathway!');
        setAdded(true);
      }
    } catch (error) {
      console.error('Failed to add to competency pathway:', error);
      toast.error('Failed to add to pathway');
    } finally {
      setAdding(false);
    }
  };
  
  if (!isAuthenticated) {
    return null;
  }
  
  if (added) {
    return (
      <Button 
        variant={variant} 
        size={size} 
        disabled
        className={`border-teal-500/50 text-teal-400 bg-teal-500/10 cursor-default ${className}`}
        data-testid={`added-to-pathway-${itemType}-${itemId}`}
      >
        <Check className="h-4 w-4 mr-1" />
        Added to Pathway
      </Button>
    );
  }
  
  return (
    <Button 
      variant={variant} 
      size={size} 
      onClick={handleAddToCompetencyPathway}
      disabled={adding}
      className={`border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:border-teal-500/50 hover:text-teal-400 transition-colors ${className}`}
      data-testid={`add-to-pathway-${itemType}-${itemId}`}
    >
      {adding ? (
        <>
          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
          Adding...
        </>
      ) : (
        <>
          <Plus className="h-4 w-4 mr-1" />
          Add to Competency Pathway
        </>
      )}
    </Button>
  );
}
